import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  List,
  ListItem,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Close as CloseIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import axios from 'axios';

const API_BASE_URL = 'https://agent.1lb6ey6g0jkt.iconaccounting.ie';

const ActiveChats = () => {
  const [activeChats, setActiveChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [socket, setSocket] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [audioContext, setAudioContext] = useState(null);
  const [showFinishChatConfirm, setShowFinishChatConfirm] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notification, setNotification] = useState(null);
  const messagesEndRef = useRef(null);
  const titleFlashIntervalRef = useRef(null);
  const originalTitleRef = useRef(document.title);
  const navigate = useNavigate();

  // Initialize audio context for notification sounds
  useEffect(() => {
    const initAudio = () => {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const context = new AudioContext();
        setAudioContext(context);
      } catch (error) {
        console.error('Audio context not supported:', error);
      }
    };

    initAudio();
  }, []);

  // Play notification sound
  const playNotificationSound = () => {
    if (!soundEnabled || !audioContext) return;

    try {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      // Louder and slightly longer notification
      gainNode.gain.linearRampToValueAtTime(0.6, audioContext.currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.02, audioContext.currentTime + 0.6);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);

      console.log('🔔 Message notification sound played');
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  // Start flashing the tab title when window is not focused
  const startTitleFlash = (text = 'New message!') => {
    if (!document.hidden) return; // Only flash when tab not active
    if (titleFlashIntervalRef.current) return; // Already flashing
    originalTitleRef.current = document.title;
    let toggle = false;
    titleFlashIntervalRef.current = setInterval(() => {
      document.title = toggle ? text : originalTitleRef.current;
      toggle = !toggle;
    }, 800);
  };

  const stopTitleFlash = () => {
    if (titleFlashIntervalRef.current) {
      clearInterval(titleFlashIntervalRef.current);
      titleFlashIntervalRef.current = null;
      document.title = originalTitleRef.current;
    }
  };

  // Initialize socket connection once
  useEffect(() => {
    const initializeSocket = () => {
      if (socket) {
        socket.disconnect();
      }

      console.log('🔌 Initializing socket connection...');
      const newSocket = io(API_BASE_URL, {
        reconnection: true,
        reconnectionDelay: 3000, // Increased delay
        reconnectionAttempts: 3, // Reduced attempts
        timeout: 30000 // Increased timeout
      });

      newSocket.on('connect', () => {
        console.log('✅ Socket connected');
        setIsConnected(true);
        const token = localStorage.getItem('authToken');
        if (token) newSocket.emit('join_support', token);
      });

      newSocket.on('disconnect', () => {
        console.log('🔌 Socket disconnected');
        setIsConnected(false);
      });

      newSocket.on('reconnect', () => {
        console.log('🔄 Socket reconnected');
        setIsConnected(true);
        const token = localStorage.getItem('authToken');
        if (token) newSocket.emit('join_support', token);
        if (selectedChat) {
          newSocket.emit('join_chat', selectedChat.session_id);
        }
      });

      newSocket.on('new_message', (message) => {
        console.log('📨 Received new message:', message);
        
        // Only add message if it belongs to the currently selected chat
        if (selectedChat && message.sessionId === selectedChat.session_id) {
          setMessages(prev => {
            // Check for duplicates
            const messageExists = prev.some(msg => msg.id === message.id);
            if (messageExists) {
              console.log('🚫 Duplicate message detected, skipping');
              return prev;
            }
            
            console.log('✅ Adding new message to chat');
            const newMessages = [...prev, message];
            
            // Play notification sound and flash tab for user messages
            if (message.sender_type === 'user') {
              playNotificationSound();
              startTitleFlash('New customer message');
            }
            
            return newMessages;
          });
        }
      });

      newSocket.on('new_chat_started', (data) => {
        console.log('🆕 New chat started:', data);
        playNotificationSound();
        // Refresh active chats when a new chat starts
        fetchActiveChats();
      });

      newSocket.on('new_message', (data) => {
        console.log('💬 New message received:', data);
        playNotificationSound();
        startTitleFlash('New customer message');
        
        // If this message is for the currently selected chat, add it to the messages
        if (selectedChat && selectedChat.session_id === data.sessionId) {
          console.log('💬 Adding new message to current chat:', data);
          const newMessage = {
            id: data.id || Date.now(),
            sessionId: data.sessionId,
            message: data.message,
            sender_type: data.sender_type,
            sender_id: data.sender_id,
            timestamp: data.timestamp || new Date().toISOString()
          };
          
          setMessages(prev => [...prev, newMessage]);
        }
        
        // Refresh active chats when a new message arrives to update timestamps
        fetchActiveChats();
      });

      newSocket.on('chat_ended', (data) => {
        console.log('🔚 Chat ended:', data);
        // Immediately remove ended chat from active list and prevent further sending
        setActiveChats(prev => prev.filter(c => c.session_id !== data.sessionId));
        
        if (selectedChat && selectedChat.session_id === data.sessionId) {
          // Immediate notification and deselect
          const endMessage = {
            id: Date.now(),
            message: data.message || 'Chat session ended',
            sender_type: 'system',
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, endMessage]);
          const endedBy = data.endedBy || 'customer';
          setNotification({
            type: 'info',
            message: endedBy === 'customer' 
              ? 'Customer disconnected. Chat moved to transcripts.'
              : 'Chat ended and moved to transcripts.',
            duration: 5000
          });
          // Deselect immediately so input is disabled and agent cannot continue
          setSelectedChat(null);
          setMessages([]);
        }
        // Also refresh lists and transcripts in the background
        fetchActiveChats();
      });

      newSocket.on('chat_finished', (data) => {
        console.log('✅ Chat finished:', data);
        // Refresh active chats when a chat is finished by support
        fetchActiveChats();
      });

      newSocket.on('chat_picked_up', (data) => {
        console.log('👨‍💼 Chat picked up:', data);
        // Refresh active chats when a chat is picked up
        fetchActiveChats();
      });

      setSocket(newSocket);
    };

    initializeSocket();

    return () => {
      if (socket) {
        socket.disconnect();
      }
      stopTitleFlash();
    };
  }, []); // Only run once on component mount

  // Initial fetch of active chats (no periodic polling needed with sockets)
  useEffect(() => {
    fetchActiveChats();
  }, []);

  // Handle chat selection
  useEffect(() => {
    if (selectedChat && socket && isConnected) {
      console.log('📱 Switching to chat:', selectedChat.session_id);
      
      // Join the chat room
      socket.emit('join_chat', selectedChat.session_id);
      
      // Load initial messages
      loadMessages(selectedChat.session_id);
    } else if (selectedChat && !isConnected) {
      // If socket is not connected, still load messages via API
      console.log('📱 Loading messages via API (socket not connected)');
      loadMessages(selectedChat.session_id);
    }
  }, [selectedChat, socket, isConnected]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Stop flashing when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        stopTitleFlash();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Auto-clear notification after duration
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, notification.duration);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchActiveChats = async () => {
    // Prevent multiple simultaneous refreshes
    if (isRefreshing) {
      console.log('🔄 Skipping refresh - already in progress');
      return;
    }

    try {
      setIsRefreshing(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/api/support/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('📋 Fetched active chats:', response.data.length);
      
      // Only update if there's actually a change
      const currentChatIds = activeChats.map(chat => chat.session_id).sort();
      const newChatIds = response.data.map(chat => chat.session_id).sort();
      
      if (JSON.stringify(currentChatIds) !== JSON.stringify(newChatIds)) {
        console.log('🔄 Active chats changed, updating...');
        setActiveChats(response.data);
        
        // Auto-select first chat if none selected
        if (response.data.length > 0 && !selectedChat) {
          console.log('🔄 Auto-selecting first active chat');
          setSelectedChat(response.data[0]);
        }
        
        // Check if currently selected chat is still in active chats
        if (selectedChat && !response.data.find(chat => chat.session_id === selectedChat.session_id)) {
          console.log('🔚 Selected chat is no longer active, clearing selection');
          setSelectedChat(null);
          setMessages([]);
        }
        
        // Clear selected chat if no active chats
        if (selectedChat && response.data.length === 0) {
          console.log('🔄 Clearing selected chat - no active chats');
          setSelectedChat(null);
          setMessages([]);
        }
      } else {
        console.log('📋 No changes in active chats, skipping update');
      }
      
    } catch (error) {
      console.error('Failed to fetch active chats:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadMessages = async (sessionId) => {
    console.log('📥 Loading messages for session:', sessionId);
    setIsLoadingMessages(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/chats/messages/${sessionId}`);
      console.log('📥 Loaded messages:', response.data.length);
      setMessages(response.data);
    } catch (error) {
      console.error('❌ Failed to load messages:', error);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !selectedChat || !socket) {
      console.log('❌ Cannot send message - missing requirements');
      return;
    }

    const messageData = {
      sessionId: selectedChat.session_id,
      message: inputMessage.trim(),
      sender_type: 'support',
      sender_id: 'agent'
    };

    console.log('📤 Sending message:', messageData);

    // Add message to local state immediately for instant display
    const tempMessage = {
      id: Date.now(),
      sessionId: selectedChat.session_id,
      message: inputMessage.trim(),
      sender_type: 'support',
      sender_id: 'agent',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, tempMessage]);
    setInputMessage('');

    try {
      // Send via socket
      socket.emit('chat_message', messageData);
      console.log('✅ Message sent via socket');
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      // Remove temporary message if failed
      setMessages(prev => prev.filter(msg => msg.id !== tempMessage.id));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const finishChat = () => {
    setShowFinishChatConfirm(true);
  };

  const confirmFinishChat = async () => {
    if (!selectedChat) return;

    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${API_BASE_URL}/api/chats/finish/${selectedChat.session_id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('✅ Chat finished successfully');
      
      setShowFinishChatConfirm(false);
      setSelectedChat(null);
      setMessages([]);
      
      // Refresh active chats
      fetchActiveChats();
      
      // Navigate back to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('❌ Failed to finish chat:', error);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
  };

  return (
    <Box>
      <AppBar 
        position="fixed"
        sx={{
          background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
          boxShadow: '0 4px 20px rgba(0, 170, 229, 0.3)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Toolbar sx={{ minHeight: '70px' }}>
          <IconButton
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ 
              mr: 2,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 600,
              fontSize: '1.25rem'
            }}
          >
            Active Chats ({activeChats.length})
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mr: 2,
            gap: 1
          }}>
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: isConnected ? '#10b981' : '#ef4444',
              animation: isConnected ? 'pulse 2s infinite' : 'none',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 }
              }
            }} />
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.75rem',
                fontWeight: isConnected ? 400 : 600
              }}
            >
              {isConnected ? 'Real-time' : 'Reconnecting...'}
            </Typography>
          </Box>
          <IconButton
            color="inherit"
            onClick={toggleSound}
            sx={{ 
              mr: 2,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 8, p: 3 }}>
        <Grid container spacing={3} sx={{ height: 'calc(100vh - 120px)' }}>
          {/* Chat List */}
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              height: '100%',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '1.25rem',
                    color: '#2d3748',
                    mb: 2
                  }}
                >
                  Active Conversations
                </Typography>
                <List sx={{ p: 0 }}>
                  {activeChats.map((chat) => (
                    <ListItem
                      key={chat.session_id}
                      button
                      selected={selectedChat?.session_id === chat.session_id}
                      onClick={() => setSelectedChat(chat)}
                      sx={{
                        borderRadius: '15px',
                        mb: 1,
                        p: 2,
                        background: selectedChat?.session_id === chat.session_id
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        color: selectedChat?.session_id === chat.session_id ? 'white' : '#2d3748',
                        boxShadow: selectedChat?.session_id === chat.session_id
                          ? '0 4px 12px rgba(102, 126, 234, 0.3)'
                          : '0 2px 8px rgba(0, 0, 0, 0.05)',
                        border: selectedChat?.session_id === chat.session_id
                          ? 'none'
                          : '1px solid rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                          background: selectedChat?.session_id === chat.session_id
                            ? 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
                            : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                          transform: 'translateY(-1px)',
                          boxShadow: selectedChat?.session_id === chat.session_id
                            ? '0 6px 16px rgba(102, 126, 234, 0.4)'
                            : '0 4px 12px rgba(0, 0, 0, 0.1)'
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography 
                          variant="subtitle1"
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '1rem',
                            mb: 0.5
                          }}
                        >
                          {chat.user_name || 'Anonymous Customer'}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            opacity: selectedChat?.session_id === chat.session_id ? 0.9 : 0.7,
                            fontSize: '0.875rem'
                          }}
                        >
                          {chat.message_count} messages
                        </Typography>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Chat Messages */}
          <Grid item xs={12} md={8}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              {selectedChat ? (
                <>
                  <CardContent sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      mb: 3,
                      p: 2.5,
                      background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
                      borderRadius: '15px',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(0, 170, 229, 0.3)'
                    }}>
                      <Typography 
                        variant="h6"
                        sx={{ 
                          fontWeight: 600,
                          fontSize: '1.1rem'
                        }}
                      >
                        Chat with {selectedChat.user_name || 'Anonymous Customer'}
                      </Typography>
                      {/* Allow agent to rename the customer inline */}
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <IconButton
                          onClick={async () => {
                            const current = selectedChat.user_name || '';
                            const name = window.prompt('Set customer name', current);
                            if (name === null) return;
                            try {
                              const token = localStorage.getItem('authToken');
                              await axios.put(`${API_BASE_URL}/api/chats/update-user/${selectedChat.session_id}`, { name, email: selectedChat.user_email || null }, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              // Reflect locally
                              setSelectedChat({ ...selectedChat, user_name: name });
                              setActiveChats(prev => prev.map(c => c.session_id === selectedChat.session_id ? { ...c, user_name: name } : c));
                            } catch (e) {
                              console.error('Failed to update name:', e);
                            }
                          }}
                          sx={{
                            color: 'white',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: '12px',
                            width: 40,
                            height: 40,
                            background: 'rgba(255, 255, 255, 0.1)',
                            '&:hover': {
                              background: 'rgba(255, 255, 255, 0.2)',
                              transform: 'scale(1.05)'
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                          title="Edit customer name"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Button
                          variant="contained"
                          startIcon={<CloseIcon />}
                          onClick={finishChat}
                          size="small"
                          sx={{
                            borderRadius: '12px',
                            px: 2,
                            py: 1,
                            background: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            textTransform: 'none',
                            fontWeight: 500,
                            '&:hover': {
                              background: 'rgba(255, 255, 255, 0.3)',
                              transform: 'scale(1.05)'
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        >
                          Finish Chat
                        </Button>
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      height: '400px', 
                      overflow: 'auto',
                      p: 2,
                      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                      borderRadius: '15px'
                    }}>
                      {isLoadingMessages ? (
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center', 
                          height: '100%' 
                        }}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Loading messages...
                          </Typography>
                        </Box>
                      ) : messages.length === 0 ? (
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center', 
                          height: '100%' 
                        }}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            No messages yet. Start the conversation!
                          </Typography>
                        </Box>
                      ) : (
                        messages.map((message, index) => (
                        <Box
                          key={`${message.id}-${message.timestamp}-${index}`}
                          sx={{
                            display: 'flex',
                            justifyContent: message.sender_type === 'support' ? 'flex-end' : 'flex-start',
                            mb: 2
                          }}
                        >
                          <Paper
                            sx={{
                              p: 2,
                              maxWidth: '70%',
                              background: message.sender_type === 'support' 
                                ? 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)'
                                : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                              color: message.sender_type === 'support' ? 'white' : '#2d3748',
                              borderRadius: '18px',
                              boxShadow: message.sender_type === 'support'
                                ? '0 4px 12px rgba(0, 170, 229, 0.3)'
                                : '0 2px 8px rgba(0, 0, 0, 0.1)',
                              border: message.sender_type === 'support' 
                                ? 'none'
                                : '1px solid rgba(0, 0, 0, 0.05)'
                            }}
                          >
                            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                              {message.message}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                opacity: message.sender_type === 'support' ? 0.8 : 0.6,
                                fontSize: '0.75rem',
                                display: 'block',
                                mt: 1
                              }}
                            >
                              {formatTime(message.timestamp)}
                            </Typography>
                          </Paper>
                        </Box>
                      ))
                      )}
                      <div ref={messagesEndRef} />
                    </Box>
                  </CardContent>
                  
                  <Box sx={{ 
                    p: 3, 
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    borderTop: '1px solid rgba(0, 0, 0, 0.05)',
                    borderBottomLeftRadius: '20px',
                    borderBottomRightRadius: '20px'
                  }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Type your message..."
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '25px',
                            backgroundColor: 'white',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                            },
                            '&.Mui-focused': {
                              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)'
                            }
                          }
                        }}
                      />
                      <IconButton
                        onClick={sendMessage}
                        disabled={!inputMessage.trim()}
                        sx={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          borderRadius: '50%',
                          width: 45,
                          height: 45,
                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                            transform: 'scale(1.05)',
                            boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)'
                          },
                          '&:disabled': {
                            background: '#e2e8f0',
                            color: '#a0aec0',
                            boxShadow: 'none'
                          }
                        }}
                      >
                        <SendIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </>
              ) : (
                <CardContent sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  p: 4
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: '#64748b',
                        fontWeight: 500,
                        mb: 1
                      }}
                    >
                      Select a chat to start messaging
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#94a3b8',
                        fontSize: '0.875rem'
                      }}
                    >
                      Choose a conversation from the list to begin
                    </Typography>
                  </Box>
                </CardContent>
              )}
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Notification */}
      {notification && (
        <Box
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 9999,
            background: notification.type === 'info' ? '#3b82f6' : '#10b981',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            animation: 'slideIn 0.3s ease-out',
            '@keyframes slideIn': {
              '0%': {
                transform: 'translateX(100%)',
                opacity: 0
              },
              '100%': {
                transform: 'translateX(0)',
                opacity: 1
              }
            }
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {notification.message}
          </Typography>
        </Box>
      )}

      {/* Finish Chat Confirmation Dialog */}
      <Dialog
        open={showFinishChatConfirm}
        onClose={() => setShowFinishChatConfirm(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
          color: 'white',
          borderRadius: '20px 20px 0 0',
          fontWeight: 600
        }}>
          Finish Chat Session
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: '1rem', lineHeight: 1.6 }}>
            Are you sure you want to finish this chat session? This will end the conversation and move it to transcripts.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button 
            onClick={() => setShowFinishChatConfirm(false)}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmFinishChat}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1,
              background: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
              color: 'white',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                background: 'linear-gradient(135deg, #c53030 0%, #a02323 100%)'
              }
            }}
          >
            Finish Chat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActiveChats;
